import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { fetchServices, fetchServicesDetails, fetchServiceStatuses, fetchServiceLog } from './api';
import type { ServiceStatus, ServiceCustomerDetailsOutput, Service, ServiceLogOutput } from './types';
import { useMutation, useQuery } from '@tanstack/react-query';

// ── Tipos de dominio ──────────────────────────────────────────────────────────

export interface DataRow {
    id: string | number;
    customerId: number;
    eiffelCustomerId: string;
    customer: string;
    business: string;
    status: string;
    statusPriority: number;
    details: DataRowDetail[];
}

export interface DataRowDetail {
    id: number;
    serviceId: number;
    service: string;
    status: string;
    statusPriority: number;
    lastRun: string;
    frequency: string;
    frequencyValue: number;
    finishDateValue: string | null;
    inactive: boolean;
}

// ── Helpers de dominio ────────────────────────────────────────────────────────

export const INACTIVE_STATUS = 'Inactivo';

export const statusColor = (status: string | null | undefined): 'success' | 'warning' | 'error' | 'primary' => {
    if (!status) return 'primary';
    const s = status.toUpperCase();
    if (s === 'OK') return 'success';
    if (s === 'ADVERTENCIA') return 'warning';
    if (s === 'ERROR') return 'error';
    return 'primary';
};

// Color para las cards/chips de la barra de estados, donde 'Inactivo' es un
// pseudo-estado gris. statusColor queda intacto porque su retorno se usa como
// indice de theme.palette y no admite 'default'.
export const statusCardColor = (result: string): 'success' | 'warning' | 'error' | 'primary' | 'default' =>
    result === INACTIVE_STATUS ? 'default' : statusColor(result);

// ── Hook principal ────────────────────────────────────────────────────────────

export const useTracing = () => {
    // ── Estado de filtros ─────────────────────────────────────────────────────────
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
    const [selectedService, setSelectedService] = useState<string>('Todos');
    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState('');

    // ── Estado de selección master-detail ────────────────────────────────────────
    const [selectedClientId, setSelectedClientId] = useState<string | number | null>(null);

    // ── Estado de UI mobile ───────────────────────────────────────────────────────
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [mobileExpandedId, setMobileExpandedId] = useState<string | number | null>(null);

    // ── Estado del log ────────────────────────────────────────────────────────────
    const [isLogOpen, setIsLogOpen] = useState(false);
    const [selectedLogService, setSelectedLogService] = useState<DataRowDetail | null>(null);

    // ── Fetch de datos ────────────────────────────────────────────────────────────
    // El daemon actualiza resultados cada 1 min, así que esta query refresca a ese ritmo
    // en vez de los 5 min del default global, y al volver a la pestaña (el default es false).
    const servicesDetailsQuery = useQuery<ServiceCustomerDetailsOutput[], Error>({
        queryKey: ['servicesDetails'],
        queryFn: fetchServicesDetails,
        refetchInterval: 60 * 1000,
        refetchOnWindowFocus: true,
    });

    const statusesQuery = useQuery<ServiceStatus[], Error>({
        queryKey: ['serviceStatuses'],
        queryFn: fetchServiceStatuses,
    });

    const servicesQuery = useQuery<Service[], Error>({
        queryKey: ['services'],
        queryFn: fetchServices,
    });

    const serviceLogMutation = useMutation<ServiceLogOutput, Error, { serviceId: number; eiffelCustomerId: string }>({
        mutationFn: fetchServiceLog,
        onError: () => {
            toast.error('No se pudo cargar el log del servicio');
        },
    });

    // ── Debounce de búsqueda ──────────────────────────────────────────────────────
    useEffect(() => {
        const handle = setTimeout(() => {
            setDebouncedSearchText(searchText.trim());
        }, 250);
        return () => clearTimeout(handle);
    }, [searchText]);

    // ── Opciones de servicios desde API ──────────────────────────────────────────
    const serviceOptions = useMemo(() => {
        const services = servicesQuery.data ?? [];
        return [
            { value: 'Todos', label: 'Todos' },
            ...services.map(s => ({ value: s.name, label: s.name })),
        ];
    }, [servicesQuery.data]);

    // ── Mapeo y ordenamiento de datos ────────────────────────────────────────────
    const tableData = useMemo<DataRow[]>(() => {
        const servicesDetails = servicesDetailsQuery.data ?? [];

        const mapped = servicesDetails.map(customerData => {
            const details: DataRowDetail[] = customerData.services.map(svc => ({
                id: svc.serviceCustomerId,
                serviceId: svc.serviceId,
                service: svc.name,
                status: svc.status,
                statusPriority: svc.statusPriority,
                lastRun: svc.lastExecution
                    ? new Date(svc.lastExecution).toLocaleString('es-AR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                    })
                    : '-',
                frequency: `${svc.frequency} min`,
                frequencyValue: svc.frequency,
                finishDateValue: svc.finishDate ?? null,
                inactive: svc.inactive,
            }));

            // El estado del cliente lo calcula el backend en /services/details: excluye
            // servicios inactivos, vencidos por fecha y sin resultado. Derivarlo acá dejaba
            // al cliente pegado a un estado viejo aunque sus servicios vigentes cambiaran.
            return {
                id: customerData.customerId,
                customerId: customerData.customerId,
                eiffelCustomerId: customerData.eiffelCustomerId,
                customer: customerData.customerName,
                business: customerData.businessName,
                status: customerData.status?.trim() || 'Indefinido',
                statusPriority: customerData.statusPriority,
                details,
            };
        });

        return mapped.sort((a, b) => {
            if (a.statusPriority !== b.statusPriority) {
                return a.statusPriority - b.statusPriority;
            }
            return a.customer.localeCompare(b.customer);
        });
    }, [servicesDetailsQuery.data]);

    // ── Cards de estado: conteo recalculado sobre servicios activos ──────────────
    const statusCards = useMemo<ServiceStatus[]>(() => {
        const counts = new Map<string, number>();
        let inactiveCount = 0;

        tableData.forEach(row => {
            row.details.forEach(detail => {
                if (detail.inactive) {
                    inactiveCount += 1;
                    return;
                }
                const key = detail.status?.toUpperCase().trim();
                if (!key) return;
                counts.set(key, (counts.get(key) ?? 0) + 1);
            });
        });

        const statuses = [...(statusesQuery.data ?? [])]
            .sort((a, b) => a.priority - b.priority)
            .map(status => ({
                ...status,
                quantity: counts.get(status.result?.toUpperCase().trim()) ?? 0,
            }));

        return [
            ...statuses,
            {
                possibleResultId: 0,
                quantity: inactiveCount,
                result: INACTIVE_STATUS,
                description: 'Servicios dados de baja',
                priority: Number.MAX_SAFE_INTEGER,
            },
        ];
    }, [statusesQuery.data, tableData]);

    // ── Opciones de estados (incluye el pseudo-estado Inactivo) ──────────────────
    const statusOptions = useMemo(() => {
        const uniqueResults = Array.from(new Set(statusCards.map(s => s.result)));
        return [
            { value: 'Todos', label: 'Todos' },
            ...uniqueResults.map(r => ({ value: r, label: r })),
        ];
    }, [statusCards]);

    // ── Filtrado por status + servicio + búsqueda ────────────────────────────────
    const filteredTableData = useMemo<DataRow[]>(() => {
        let data = tableData;

        const normalizedStatus = selectedStatus?.toUpperCase();
        const normalizedService =
            selectedService && selectedService !== 'Todos'
                ? selectedService.toLowerCase()
                : null;

        const normalizedSearch = debouncedSearchText.toLowerCase();
        if (normalizedSearch) {
            data = data.filter(row =>
                row.customer.toLowerCase().includes(normalizedSearch) ||
                row.business.toLowerCase().includes(normalizedSearch)
            );
        }

        if (normalizedService) {
            data = data.filter(row =>
                row.details.some(d => !d.inactive && d.service.toLowerCase() === normalizedService)
            );
        }

        if (normalizedStatus === INACTIVE_STATUS.toUpperCase()) {
            data = data.filter(row => row.details.some(d => d.inactive));
        } else if (normalizedStatus) {
            data = data.filter(row =>
                row.details.some(d => !d.inactive && d.status?.toUpperCase() === normalizedStatus)
            );
        }

        return data;
    }, [tableData, selectedStatus, selectedService, debouncedSearchText]);

    // ── Garantizar cliente seleccionado visible ───────────────────────────────────
    useEffect(() => {
        if (filteredTableData.length === 0) return;
        const currentIsVisible = filteredTableData.some(c => c.id === selectedClientId);
        if (!selectedClientId || !currentIsVisible) {
            setSelectedClientId(filteredTableData[0].id);
        }
    }, [filteredTableData, selectedClientId]);

    const selectedClient = useMemo<DataRow | undefined>(
        () => filteredTableData.find(client => client.id === selectedClientId),
        [filteredTableData, selectedClientId]
    );

    const selectedClientRaw = useMemo<DataRow | undefined>(
        () => tableData.find(client => client.id === selectedClientId),
        [tableData, selectedClientId]
    );

    // ── Derived: detalles del log ordenados por fecha desc ───────────────────────
    const logDetails = useMemo(() =>
        (serviceLogMutation.data?.details ?? []).slice().sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        ),
        [serviceLogMutation.data]
    );

    // ── Handlers: filtros ─────────────────────────────────────────────────────────
    const handleStatusClick = (result?: string) => {
        if (!result) return;
        const normalized = result.toUpperCase();
        if (selectedStatus && selectedStatus.toUpperCase() === normalized) {
            setSelectedStatus(null);
            return;
        }
        setSelectedStatus(result);
    };

    const handleStatusChange = (value: string) => {
        if (value === 'Todos') {
            setSelectedStatus(null);
            return;
        }
        setSelectedStatus(value);
    };

    const handleSearchChange = (value: string) => setSearchText(value);

    const handleServiceChange = (value: string) => setSelectedService(value);

    const handleSelectClient = (id: string | number) => setSelectedClientId(id);

    const handleMobileToggle = (id: string | number) => {
        setMobileExpandedId(prev => (prev === id ? null : id));
        setSelectedClientId(id);
    };

    const handleClearFilters = () => {
        setSelectedStatus(null);
        setSelectedService('Todos');
        setSearchText('');
        if (tableData.length > 0) {
            setSelectedClientId(tableData[0].id);
        }
    };

    // ── Handlers: log ─────────────────────────────────────────────────────────────
    const handleOpenLog = (service: DataRowDetail) => {
        if (!selectedClientRaw?.eiffelCustomerId) return;

        setSelectedLogService(service);
        setIsLogOpen(true);
        serviceLogMutation.mutate({
            serviceId: service.serviceId,
            eiffelCustomerId: selectedClientRaw.eiffelCustomerId,
        });
    };

    const handleCloseLog = () => {
        setIsLogOpen(false);
        setSelectedLogService(null);
    };

    return {
        // Data
        tableData: filteredTableData,
        selectedClient,
        statuses: statusCards,
        services: servicesQuery.data ?? [],

        // Estado de carga
        servicesLoading: servicesDetailsQuery.isLoading,
        servicesError: servicesDetailsQuery.error,
        statusesLoading: statusesQuery.isLoading,
        statusesError: statusesQuery.error,

        // Filtros
        selectedClientId,
        selectedStatus,
        selectedService,
        searchText,
        serviceOptions,
        statusOptions,

        // Log
        isLogOpen,
        selectedLogService,
        logDetails,
        logLoading: serviceLogMutation.isPending,
        logError: serviceLogMutation.error,

        // UI mobile
        filtersOpen,
        setFiltersOpen,
        mobileExpandedId,
        handleMobileToggle,

        // Handlers
        handleStatusClick,
        handleStatusChange,
        handleSearchChange,
        handleServiceChange,
        handleSelectClient,
        handleClearFilters,
        handleOpenLog,
        handleCloseLog,
    };
};
